import * as React from 'react';
import { connect } from 'react-redux';

import PromptExit from 'libs/router/prompt-exit/PromptExit';
import { navigate } from 'libs/router';

import { Form, FormField } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import Input from 'components/inputs/input/Input';
import { ModalFrame, ModalFrameEntity, ModalFrameFooter, ModalFrameInner, ModalFrameMessage } from 'components/modal';

import { cancelSubcampaign } from 'modules/campaigns/reducers/campaignSubcampaigns';
import { danger } from 'redux-store/reducers/common-reducer';

import { __t, applyTranslations } from 'utils/language';
import { isRequired } from 'utils/validators';

import './subcampaign-cancellation-feedback-modal.scss';

applyTranslations(require('./subcampaign-cancellation-feedback-modal.i18n.json'));

import type { CampaignType, Subcampaign, SubcampaignDisagreementDetails } from 'types';

const OFFER_CANCEL_OPTIONS = [
  'stopped_responding',
  'agreed_unintentionally',
  'requirements_changed',
  'other'
] as const;

type AllCancellationOptions = Readonly<typeof OFFER_CANCEL_OPTIONS>;
type WithoutAgreedCancellationOptions = Readonly<[
  (typeof OFFER_CANCEL_OPTIONS)[0],
  (typeof OFFER_CANCEL_OPTIONS)[2],
  (typeof OFFER_CANCEL_OPTIONS)[3]
]>
type WithCcmCancellationOptions = Readonly<[
  (typeof OFFER_CANCEL_OPTIONS)[1],
  (typeof OFFER_CANCEL_OPTIONS)[2],
  (typeof OFFER_CANCEL_OPTIONS)[3]
]>
type CancellationOptions =
  | AllCancellationOptions
  | WithoutAgreedCancellationOptions
  | WithCcmCancellationOptions;

type OwnProps = {
  subcampaign: Subcampaign,
  campaignType: CampaignType,
  redirectTo?: string,
  onCloseModal: () => void
};

type DispatchProps = {
  dispatchCancelSubcampaign: (
    subcampaign: Subcampaign,
    disagreementDetails: SubcampaignDisagreementDetails,
    campaignType: CampaignType
  ) => Promise<void>,
  dispatchDanger: () => void
};

type Props = OwnProps & DispatchProps;

type State = {
  reason: string,
  comment: string,
  isModified: boolean
};

const MSG_KEY = 'subcampaign_cancellation_feedback_modal';

class SubcampaignCancellationFeedbackModal extends React.PureComponent<Props, State> {
  static defaultProps = {
    campaignType: 'collab' as CampaignType
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      reason: '',
      comment: '',
      isModified: false
    };
  }

  handleSubmit = (): void => {
    const { dispatchCancelSubcampaign, onCloseModal, subcampaign, campaignType, redirectTo } = this.props;
    const { comment, reason } = this.state;
    const reasonLabel = this.getReason(reason);

    onCloseModal();
    dispatchCancelSubcampaign(subcampaign, {
      reason: reasonLabel,
      comment
    }, campaignType)
      .then(() => {
        if (redirectTo) {
          navigate.to(redirectTo);
        }
      });
  };

  handleValidationErrors = (): void => this.props.dispatchDanger();

  handleReasonChange = (value: string): void => {
    this.setState({
      reason: value,
      isModified: true
    });
  };

  handleCommentChange = (event: Event): void => {
    const input = event.target;
    const value = (input instanceof HTMLTextAreaElement && input.value) || '';

    this.setState({
      comment: String(value),
      isModified: true
    });
  };

  getReason = (value: string): string => {
    if (value === 'other') {
      return __t(`${MSG_KEY}.options.other_reason`);
    }
    return __t(`${MSG_KEY}.options[${value}]`);
  };

  getCancallationOptions = (subcampaignState: Subcampaign['state']): CancellationOptions => {
    const { campaignType } = this.props;

    if (campaignType === 'ccm') {
      return [
        OFFER_CANCEL_OPTIONS[1],
        OFFER_CANCEL_OPTIONS[2],
        OFFER_CANCEL_OPTIONS[3]
      ];
    }

    if (subcampaignState === 'accepted') {
      return OFFER_CANCEL_OPTIONS;
    }

    return [
      OFFER_CANCEL_OPTIONS[0],
      OFFER_CANCEL_OPTIONS[2],
      OFFER_CANCEL_OPTIONS[3]
    ];
  };

  render() {
    const { reason, comment, isModified } = this.state;
    const { subcampaign, campaignType, onCloseModal } = this.props;
    const cancellationOptions = this.getCancallationOptions(subcampaign.state);

    return (
      <ModalFrame
        title={__t(`${MSG_KEY}.title`)}
        subtitle={campaignType !== 'ccm' ? __t(`${MSG_KEY}.subtitle`) : null}
      >
        <ModalFrameEntity
          influencerProfile={subcampaign.creatorProfile}
        />
        <ModalFrameInner
          narrowContent
        >
          <Form
            onSubmit={this.handleSubmit}
            onValidationErrors={this.handleValidationErrors}
          >
            {
              isModified &&
                <PromptExit type="modal" />
            }
            <FormField
              label={__t(`${MSG_KEY}.options.title`)}
              showRequiredMark
              validators={[isRequired(reason)]}
              direction="column"
              labelType="highlighted"
              fontSize="small"
              tooltipMessage={__t(`${MSG_KEY}.options.tooltip`)}
            >
              <MixedSelectDropdown
                className="subcampaign-cancellation-feedback-modal__reason"
                name="offer-cancel-reason"
                items={cancellationOptions.map((value) => ({
                  identifier: value,
                  isDisabled: false,
                  isSelected: value === reason,
                  label: __t(`${MSG_KEY}.options[${value}]`),
                  value
                }))}
                placeholder={__t(`${MSG_KEY}.options.placeholder`)}
                onItemsToggle={(items) => {
                  this.handleReasonChange(items.find(item => item.isSelected)?.value ?? reason);
                }}
              />
            </FormField>
            <FormField
              label={__t(`${MSG_KEY}.additional_message`)}
              direction="column"
              labelType="highlighted"
              fontSize="small"
              showValidationIcon={false}
            >
              <Input
                value={comment}
                placeholder={__t(`${MSG_KEY}.additional_message_placeholder`)}
                onChange={this.handleCommentChange}
                multiline
                width="responsive"
              />
            </FormField>
            <ModalFrameMessage
              message={__t(`${MSG_KEY}.footer_text`)}
            />
            <ModalFrameFooter>
              <ActionButton
                color="tertiary"
                onClick={onCloseModal}
              >
                {__t(`${MSG_KEY}.actions_cancel`)}
              </ActionButton>
              <ActionButton
                type="submit"
              >
                {__t(`${MSG_KEY}.actions_confirm`)}
              </ActionButton>
            </ModalFrameFooter>
          </Form>
        </ModalFrameInner>
      </ModalFrame>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchCancelSubcampaign: (
    subcampaign: Subcampaign,
    disagreementDetails: SubcampaignDisagreementDetails,
    campaignType: CampaignType
  ): Promise<void> => (
    dispatch(cancelSubcampaign(subcampaign, disagreementDetails, campaignType))
  ),
  dispatchDanger: (): void => dispatch(danger(__t(`${MSG_KEY}.missing_fields`)))
});

export default connect(null, mapDispatchToProps)(SubcampaignCancellationFeedbackModal);
